import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Button,
  Card,
  Row,
  Col,
  InputGroup,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { listAll, ref } from "firebase/storage";
import { storage } from "../../firebase";
import {
  faFolder,
  faFolderOpen,
  faMagnifyingGlass,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../firebaseContext";

export default function ViewFiles() {
  const listRef = useMemo(() => ref(storage, "Viewable Files"), []);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterText, setFilterText] = useState("");
  const navigate = useNavigate();
  const { admin } = useAuth();

  useEffect(() => {
    async function fetchFolders() {
      setLoading(true);
      setError("");

      try {
        const res = await listAll(listRef);
        const mapped = res.prefixes
          .map((folderRef) => ({
            name: folderRef.name,
            reference: folderRef,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setFolders(mapped);
      } catch (err) {
        setError("Unable to load folders right now. Please try again later.");
        setFolders([]);
      } finally {
        setLoading(false);
      }
    }

    fetchFolders();
  }, [listRef]);

  function handleSelectFolder(reference) {
    navigate("/file/" + reference.name, {
      state: {
        fullPath: reference.fullPath,
      },
    });
  }
  function handleAdminButton(e) {
    e.preventDefault();
    if (e.target.id === "addFolder") {
      navigate("/addFolder", {
        state: {
          path: listRef.fullPath,
        },
      });
    }
  }

  const filteredFolders = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) {
      return folders;
    }
    return folders.filter((folder) =>
      folder.name.toLowerCase().includes(query)
    );
  }, [folders, filterText]);

  const totalFolders = folders.length;
  const visibleFolders = filteredFolders.length;
  const showEmptyState = !loading && visibleFolders === 0;

  return (
    <Container fluid className="py-5 view-files-page">
      <Row className="justify-content-center">
        <Col xl={10} lg={11}>
          <Card className="border-0 shadow-sm text-white bg-primary mb-4">
            <Card.Body>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                <div>
                  <Badge bg="light" text="dark" className="text-uppercase mb-2">
                    Document hub
                  </Badge>
                  <h1 className="display-6 fw-semibold mb-1">Viewable files</h1>
                  <p className="mb-0">
                    Explore curated folders of ready-to-download resources.
                    Choose a collection to browse its contents.
                  </p>
                </div>
                <div className="text-lg-end">
                  <p className="text-uppercase small text-white-50 mb-1">
                    Total collections
                  </p>
                  <h2 className="fw-bold mb-0">
                    <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
                    {totalFolders}
                  </h2>
                </div>
              </div>
              {admin && (
                <div className="d-flex flex-wrap gap-2 mt-4">
                  <Button
                    variant="light"
                    className="text-primary fw-semibold"
                    onClick={handleAdminButton}
                    id="addFolder"
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Create folder & upload files
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                <div>
                  <h2 className="h4 mb-1">Collections directory</h2>
                  <p className="text-muted mb-0">
                    Showing {visibleFolders} of {totalFolders} collections.
                  </p>
                </div>
                <InputGroup className="w-auto">
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search collections"
                    value={filterText}
                    onChange={(event) => setFilterText(event.target.value)}
                    disabled={loading || !totalFolders}
                  />
                  {filterText && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setFilterText("")}
                      disabled={loading}
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
              </div>

              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : showEmptyState ? (
                <div className="py-5 text-center text-muted border rounded-4">
                  <p className="mb-1 fw-semibold">No collections found.</p>
                  <p className="mb-0">
                    Try adjusting your search or clear the filter to view all
                    folders.
                  </p>
                </div>
              ) : (
                <Row className="g-4">
                  {filteredFolders.map((folder) => (
                    <Col key={folder.reference.fullPath} xs={12} md={6} lg={4}>
                      <Card className="h-100 border-0 rounded-4 shadow-sm">
                        <Card.Body className="d-flex flex-column gap-3 p-4">
                          <div className="d-inline-flex align-items-center gap-3">
                            <span className="badge rounded-pill text-bg-light text-primary-emphasis px-3 py-2 fs-5">
                              <FontAwesomeIcon icon={faFolder} />
                            </span>
                            <div>
                              <Card.Title className="mb-0 text-capitalize">
                                {folder.name}
                              </Card.Title>
                              <Card.Text className="text-muted mb-0 small">
                                Click to view individual files inside this
                                collection.
                              </Card.Text>
                            </div>
                          </div>
                          <Button
                            variant="primary"
                            className="mt-auto align-self-start rounded-pill fw-semibold px-3"
                            onClick={(event) => {
                              event.preventDefault();
                              handleSelectFolder(folder.reference);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faFolderOpen}
                              className="me-2"
                            />
                            Open collection
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
